/**
 * Steering Modes Configuration Component
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import {
  Tune as TuneIcon,
  Router as ExternalIcon,
  Satellite as SatelliteIcon,
  Flight as AdsbIcon,
  Hub as MultiIcon,
  Merge as CombinedIcon,
} from '@mui/icons-material';

import { DeviceConfigCreate, STEERING_MODES, calculateIsAdsbEnabled } from '../../types/config';
import { forcedController, isModeAllowed } from '../../types/levels';

interface SteeringModesProps {
  formData: DeviceConfigCreate;
  onChange: (data: DeviceConfigCreate) => void;
  disabled?: boolean;
}

const getSteeringModeIcon = (mode: string) => {
  switch (mode) {
    case 'external_reference':
      return <ExternalIcon />;
    case 'single_channel_gnss':
      return <SatelliteIcon />;
    case 'multi_channel_gnss':
      return <MultiIcon />;
    case 'adsb':
      return <AdsbIcon />;
    case 'gnss_adsb':
      return <CombinedIcon />;
    default:
      return <TuneIcon />;
  }
};

const getSteeringModeDescription = (mode: string) => {
  switch (mode) {
    case 'external_reference':
      return 'Use an external time reference signal for steering';
    case 'single_channel_gnss':
      return 'Use a single GNSS receiver channel for time steering';
    case 'multi_channel_gnss':
      return 'Use multiple GNSS receiver channels for improved accuracy';
    case 'adsb':
      return 'Use ADS-B signals for time steering';
    case 'gnss_adsb':
      return 'Combine both GNSS and ADS-B signals for optimal steering';
    default:
      return '';
  }
};

const SteeringModes: React.FC<SteeringModesProps> = ({ 
  formData, 
  onChange, 
  disabled = false 
}) => {
  const handleSteeringModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMode = event.target.value as DeviceConfigCreate['steering_mode'];
    onChange({
      ...formData,
      steering_mode: newMode,
      controller_type: forcedController(newMode),
    });
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <TuneIcon color="primary" />
          <Typography variant="h6" component="h3">
            Steering Modes
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select the primary steering mode for this MODO device. This determines which signals 
          will be used for time and frequency steering.
        </Typography>

        <FormControl component="fieldset" fullWidth disabled={disabled}>
          <FormLabel component="legend" sx={{ mb: 2 }}>
            Available Steering Modes
          </FormLabel>
          
          <RadioGroup
            value={formData.steering_mode}
            onChange={handleSteeringModeChange}
            sx={{ gap: 1 }}
          >
            {STEERING_MODES.map((mode) => {
              const allowed = isModeAllowed(formData.level, mode.value);
              return (
              <Paper 
                key={mode.value} 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  borderColor: formData.steering_mode === mode.value ? 'primary.main' : 'divider',
                  bgcolor: formData.steering_mode === mode.value ? 'primary.50' : 'transparent',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'primary.50',
                  }
                }}
              >
                <Tooltip describeChild title={allowed ? '' : `Not allowed at level ${formData.level}`} placement="top-start">
                <FormControlLabel
                  value={mode.value}
                  control={<Radio />}
                  disabled={disabled || !allowed}
                  label={
                    <Box display="flex" alignItems="center" gap={2} width="100%">
                      <Box display="flex" alignItems="center" gap={1}>
                        {getSteeringModeIcon(mode.value)}
                        <Typography variant="subtitle1" fontWeight="medium">
                          {mode.label}
                        </Typography>
                      </Box>
                      {formData.steering_mode === mode.value && (
                        <Chip 
                          label="Selected" 
                          size="small" 
                          color="primary" 
                          variant="filled"
                        />
                      )}
                    </Box>
                  }
                  sx={{ 
                    m: 0, 
                    width: '100%',
                    alignItems: 'flex-start',
                  }}
                />
                </Tooltip>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ ml: 5, mt: 0.5 }}
                >
                  {getSteeringModeDescription(mode.value)}
                </Typography>

                {/* Open Loop extra input when selected */}
                {formData.steering_mode === 'open_loop' && mode.value === 'open_loop' && (
                  <Box sx={{ ml: 5, mt: 2 }}>
                    <TextField
                      label="Open Loop Command (integer)"
                      type="number"
                      value={(formData as any).openloop_command ?? 0}
                      onChange={(e) => {
                        const val = parseInt(e.target.value || '0', 10);
                        onChange({ ...formData, openloop_command: Number.isNaN(val) ? 0 : val });
                      }}
                      inputProps={{ step: 1, min: 0 }}
                      helperText="Fixed digital command sent to OCXO when Open Loop is selected"
                      fullWidth
                    />
                  </Box>
                )}
              </Paper>
              );
            })}
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 3 }} />

        {/* Show info about ADS-B being automatically enabled */}
        {calculateIsAdsbEnabled(formData.steering_mode) && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>ADS-B Enabled:</strong> ADS-B integration is automatically enabled for this steering mode.
            </Typography>
          </Alert>
        )}

        <Alert severity="info">
          <Typography variant="body2">
            <strong>Note:</strong> Changing the steering mode may require additional configuration 
            adjustments in other tabs. After selecting a mode, review the GNSS and Controller 
            settings to ensure compatibility.
          </Typography>
        </Alert>
      </Paper>
    </Box>
  );
};

export default SteeringModes; 